import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatLabel } from "@/lib/api";
import { Calendar, MapPin } from "lucide-react";

const IMPACT_DOT = {
  restricts: "bg-[#D32F2F]",
  protects: "bg-[#008A00]",
  mixed: "bg-[#0050FF]",
};

export default function Timeline() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/entries", { params: { sort: "date_desc", limit: 500 } })
      .then((r) => setEntries(r.data))
      .catch((err) => console.error("Failed to load timeline entries", err))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const e of entries) {
      const year = (e.date_action || "").slice(0, 4) || "Undated";
      if (!m.has(year)) m.set(year, []);
      m.get(year).push(e);
    }
    return Array.from(m.entries());
  }, [entries]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12">
      <div className="border-b border-black/15 pb-6 mb-10">
        <div className="overline">/the chronology</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-2">Timeline</h1>
        <p className="text-base text-[#333] mt-3 max-w-2xl">
          A reverse-chronological ledger of every documented action. Red dots mark restrictions, green mark protections.
        </p>
      </div>

      {loading ? (
        <p className="font-mono-plex text-sm text-[#555] uppercase tracking-wider">Loading…</p>
      ) : (
        <div className="relative">
          <div className="absolute left-2 md:left-1/2 top-0 bottom-0 w-px bg-black/20" aria-hidden />
          {grouped.map(([year, items]) => (
            <div key={year} className="mb-12">
              <div className="sticky top-20 z-10 mb-6 inline-block bg-black text-white px-4 py-2 font-display text-2xl font-black ml-6 md:ml-[calc(50%-2.5rem)]">
                {year}
              </div>
              {items.map((e, i) => (
                <TimelineItem key={e.id} entry={e} side={i % 2 === 0 ? "left" : "right"} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TimelineItem({ entry, side }) {
  const sideClass = side === "left"
    ? "md:pr-12 md:text-right md:mr-1/2"
    : "md:pl-12 md:ml-1/2";
  return (
    <div className="relative grid md:grid-cols-2 gap-2 mb-6">
      {side === "left" ? (
        <>
          <Item entry={entry} alignRight />
          <div />
        </>
      ) : (
        <>
          <div />
          <Item entry={entry} />
        </>
      )}
      <span
        className={`absolute left-2 md:left-1/2 top-6 -translate-x-1/2 w-3 h-3 ${IMPACT_DOT[entry.impact] || "bg-black"}`}
        aria-hidden
      />
    </div>
  );
}

function Item({ entry, alignRight }) {
  return (
    <Link
      to={`/entry/${entry.id}`}
      data-testid={`timeline-item-${entry.id}`}
      className={`block bg-white border border-black/15 hover:border-black/60 transition p-5 ml-8 md:ml-0 ${alignRight ? "md:mr-8" : "md:ml-8"}`}
    >
      <div className="font-mono-plex text-[11px] tracking-[0.25em] uppercase text-[#555] flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" />{entry.date_action}</span>
        <span className="inline-flex items-center gap-1.5"><MapPin className="w-3 h-3" />{entry.jurisdiction}</span>
        <span>{formatLabel(entry.category)}</span>
      </div>
      <div className="font-display text-xl font-bold tracking-tight mt-2 leading-tight">{entry.title}</div>
      <div className="text-sm text-[#333] mt-2 line-clamp-2">{entry.summary}</div>
    </Link>
  );
}
